import React, { useCallback, useEffect } from 'react';
import { MapControl, ControlPosition, useMap, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import { MdMyLocation } from 'react-icons/md';
import * as translator from '../../utils/translator';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';
import { calculateDistance } from './mapUtils';
import useIsMobile from '../../customHooks/useIsMobile';

type LatLng = { lat: number; lng: number };

interface MapControlsProps {
  myLocation: LatLng;
  setMyLocation: (location: LatLng) => void;
  allPharmaciesLocations: { [stockCode: string]: { name: string, address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; } };
  onLocationUsed?: (userLocation: LatLng, nearbyPharmacies: Array<{ stockCode: string; name: string; address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; distance: number }>) => void;
  mapInstanceRef: React.MutableRefObject<any>;
  hasInitialized: React.MutableRefObject<boolean>;
  language: string;
  isPharmacyModalOpen: boolean;
  onPharmacyModalClose: () => void;
}

export const MapControls: React.FC<MapControlsProps> = ({
  myLocation,
  setMyLocation,
  allPharmaciesLocations,
  onLocationUsed,
  mapInstanceRef,
  hasInitialized,
  language,
  isPharmacyModalOpen,
  onPharmacyModalClose
}) => {
  const map = useMap('main-map');
  const { customerState } = useCustomer();
  const isMobile = useIsMobile();

  // Set map instance ref and initialize map center (only once)
  useEffect(() => {
    if (map) {
      mapInstanceRef.current = map;
      
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        
        // Priority 1: Try geolocation if permitted (without prompting)
        if (navigator.geolocation && 'permissions' in navigator) {
          navigator.permissions.query({ name: 'geolocation' }).then((result) => {
            if (result.state === 'granted') {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const coords: LatLng = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  };
                  setMyLocation(coords);
                  map.panTo(coords);
                  map.setZoom(13);
                },
                () => {
                }, 
                { timeout: 5000, enableHighAccuracy: true }
              );
            }
          }).catch(() => {
          });
        }
      }
    }
  }, [map]);

  const handleUseLocation = useCallback(() => {
    // Track Geo_Click event (user clicks "Use my location" button)
    const userId = customerState?.customerID || null;
    MainTracker.track("custom", "Geo_Click", {
      user_id: userId,
      geo_source: "button",
      screen: "express_shipping"
    });

    onPharmacyModalClose();

    // Try to get user's actual location from browser
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: LatLng = {
            lat: position.coords.latitude,  
            lng: position.coords.longitude,
          };
          
          // Track Geo_Permission_Result event (permission granted)
          MainTracker.track("custom", "Geo_Permission_Result", {
            user_id: userId,
            geo_permission: 'granted',
            lat_lng_precision: position.coords.accuracy || null,
            geo_source: 'browser'
          });
          
          // Track geo_confirmed event
          MainTracker.track("custom", "geo_confirmed", {
            user_id: userId,
            latitude: coords.lat,
            longitude: coords.lng,
            geo_source: 'browser'
          });
          
          setMyLocation(coords);
          map?.panTo(coords);
          map?.setZoom(14);

          // Filter pharmacies within 10 miles (16 km) radius
          const RADIUS_KM = 16; // 10 miles ≈ 16 km
          const nearbyPharmacies = Object.entries(allPharmaciesLocations)
            .map(([stockCode, pharmacy]) => {
              if (!pharmacy.location) return null;
              const distance = calculateDistance(
                coords.lat,
                coords.lng,
                pharmacy.location.lat,
                pharmacy.location.lng
              );
              if (distance <= RADIUS_KM) {
                return {
                  stockCode,
                  name: pharmacy.name,
                  address: pharmacy.address,
                  location: pharmacy.location,
                  is_external_pharmacy: pharmacy.is_external_pharmacy,
                  opening_hours: pharmacy.opening_hours,
                  distance,
                };
              }
              return null;
            })
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .sort((a, b) => a.distance - b.distance); // Sort by distance

          // Notify parent component
          if (onLocationUsed) {
            onLocationUsed(coords, nearbyPharmacies);
          }
        },
        (error) => {
          // Track Geo_Permission_Result event (permission denied or other error)
          MainTracker.track("custom", "Geo_Permission_Result", {
            user_id: userId,
            geo_permission: 'denied',
            lat_lng_precision: null,
            geo_source: 'browser'
          });
          
          // If geolocation fails, fallback to default location
          
          // Fallback: Use first pharmacy location if available, otherwise Berlin center
          let coords: LatLng;
          
          const firstPharmacyWithLocation = Object.values(allPharmaciesLocations).find(
            (pharmacy) => pharmacy.location
          );
          
          if (firstPharmacyWithLocation?.location) {
            coords = firstPharmacyWithLocation.location;
          } else {
            coords = { lat: 52.520008, lng: 13.404954 };
          }
          
          setMyLocation(coords);
          map?.panTo(coords);
          map?.setZoom(14);

          // Filter pharmacies within 10 miles (16 km) radius
          const RADIUS_KM = 16;
          const nearbyPharmacies = Object.entries(allPharmaciesLocations)
            .map(([stockCode, pharmacy]) => {
              if (!pharmacy.location) return null;
              const distance = calculateDistance(
                coords.lat,
                coords.lng,
                pharmacy.location.lat,
                pharmacy.location.lng
              );
              if (distance <= RADIUS_KM) {
                return {
                  stockCode,
                  name: pharmacy.name,
                  address: pharmacy.address,
                  location: pharmacy.location,
                  is_external_pharmacy: pharmacy.is_external_pharmacy,
                  opening_hours: pharmacy.opening_hours,
                  distance,
                };
              }
              return null;
            })
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .sort((a, b) => a.distance - b.distance);

          if (onLocationUsed) {
            onLocationUsed(coords, nearbyPharmacies);
          }
        }
      );
    } else {
      // Geolocation not supported, use fallback
      let coords: LatLng;
      
      const firstPharmacyWithLocation = Object.values(allPharmaciesLocations).find(
        (pharmacy) => pharmacy.location
      );
      
      if (firstPharmacyWithLocation?.location) {
        coords = firstPharmacyWithLocation.location;
      } else {
        coords = { lat: 52.520008, lng: 13.404954 };
      }
      
      setMyLocation(coords);
      map?.panTo(coords);
      map?.setZoom(14);

      const RADIUS_KM = 16;
      const nearbyPharmacies = Object.entries(allPharmaciesLocations)
        .map(([stockCode, pharmacy]) => {
          if (!pharmacy.location) return null;
          const distance = calculateDistance(
            coords.lat,
            coords.lng,
            pharmacy.location.lat,
            pharmacy.location.lng
          );
          if (distance <= RADIUS_KM) {
            return {
              stockCode,
              name: pharmacy.name,
              address: pharmacy.address,
              location: pharmacy.location,
              is_external_pharmacy: pharmacy.is_external_pharmacy,
              opening_hours: pharmacy.opening_hours,
              distance,
            };
          }
          return null;
        })
        .filter((p): p is NonNullable<typeof p> => p !== null)
        .sort((a, b) => a.distance - b.distance);

      if (onLocationUsed) {
        onLocationUsed(coords, nearbyPharmacies);
      }
    }
  }, [map, allPharmaciesLocations, onLocationUsed]);

  return (
    <>
      {/* Custom button overlay */}
      <MapControl position={ControlPosition.TOP_LEFT}>
        <button
          id="use-current-location-button"
          onClick={handleUseLocation}
          className="flex items-center gap-2 bg-white rounded-full shadow-md p-2 border-none hover:bg-gray-100"
          style={{
            margin: '12px',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <MdMyLocation className="w-6 h-6 text-[#269C6E]" />
          {(!isPharmacyModalOpen || !isMobile) && <span className="font-poppins text-[13px]">{translator.translate(language, 'choose_treatment', 'default/use_current_location')}</span>}
        </button>
      </MapControl>

      {/* Marker for user's location */}
      {myLocation && (
        <AdvancedMarker position={myLocation}>
          <Pin background={'#1E90FF'} glyphColor={'#FFF'} borderColor={'#1E90FF'} scale={1.2} />
        </AdvancedMarker>
      )}
    </>
  );
};

